import { Button, DatePicker, Divider, Form, Input, InputNumber, Modal, Pagination, Select, Space, Table, TableProps } from 'antd'
import { SearchOutlined, UndoOutlined } from "@ant-design/icons"
import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { useLoading } from '../../context/LoadingContext'
import { apiGetListPartner, apiGetListTransactionRetry, apiTransactionRetry } from '../../api'
import { toastError, toastSuccess } from '../../utils/toast'
import { mapCompanyAccounts } from '../../assets/constants/constants'
import defaultParams from '../../assets/constants/default_params'

export default function TransactionRetry() {
    const [customers, setCustomers] = useState<any>([])
    const [data, setData] = useState<any>()
    const [search, setSearch] = useState<any>({})
    const [currentPage, setCurrentPage] = useState<number>(defaultParams.PAGE)
    const [pageSize, setPageSize] = useState<number>(defaultParams.LIMIT)
    const [selectedRows, setSelectedRows] = useState<any>([])
    const [modalRetry, setModalRetry] = useState<{ data: any, isOpen: boolean }>({
        data: null,
        isOpen: false,
    })
    const [isOpenRetrySelected, setIsOpenRetrySelected] = useState<boolean>(false)
    const [modalDetail, setModalDetail] = useState<{ data: any, isOpen: boolean }>({
        data: null,
        isOpen: false,
    })
    const [otpConfirm, setOtpConfirm] = useState<string>('')
    const [formSearch] = Form.useForm()
    const { setIsLoading } = useLoading()

    const initSearch = () => {
        const date = dayjs()
        setSearch({
            year: date?.year(),
            month: date?.month() + 1,
            day: date.date(),
        })
    }

    useEffect(() => {
        initSearch()
    }, [])

    const sponsorColumns: TableProps<any>['columns'] = [
        {
            title: 'TranId',
            dataIndex: 'transaction',
            key: 'tranId',
            render: transaction => transaction.tranId,
        },
        {
            title: 'ReqTime',
            dataIndex: 'transaction',
            key: 'reqTime',
            render: transaction => dayjs(transaction.reqTime).format("YYYY-MM-DD HH:mm:ss"),
        },
        {
            title: 'Platform',
            dataIndex: 'transaction',
            key: 'platform',
            render: transaction => transaction.platform,
        },
        {
            title: 'Msisdn',
            dataIndex: 'transaction',
            key: 'msisdn',
            render: transaction => transaction.msisdn,
        },
        {
            title: 'PackageName',
            dataIndex: 'transaction',
            key: 'packageName',
            render: transaction => transaction.packageName,
        },
        {
            title: 'DataPack',
            dataIndex: 'transaction',
            key: 'dataPack',
            render: transaction => transaction.dataPack,
        },
        {
            title: 'Type',
            dataIndex: 'transaction',
            key: 'type',
            render: transaction => transaction.type,
        },
        {
            title: 'CpCode',
            dataIndex: 'transaction',
            key: 'cpCode',
            render: transaction => transaction.cpCode,
        },
        {
            title: 'Return',
            dataIndex: 'transaction',
            key: 'return',
            render: transaction => transaction.return === 'Refund' ? '' : transaction.return,
        },
        {
            title: 'Price',
            dataIndex: 'transaction',
            key: 'price',
            render: (_, data: any) => data.transaction?.type === 'REGISTER_COMBO_PROMOTION' ? 0 : data.transaction?.price,
        },
        {
            title: 'Result',
            dataIndex: 'transactionsRetry',
            key: 'result',
            render: (_, data: any) => {
                const result = data.transactionsRetry?.find((item: any) => item.return === 'SUCCESS' || item.return === 'FAIL')
                return result ? (
                    <span style={{ color: result.return === 'SUCCESS' ? '#129c37' : '#d50000' }}>{result.return}</span>
                ) : <></>
            },
        },
        {
            key: 'action',
            title: 'Action',
            align: 'center',
            render: (_, data: any) => {
                const isRetried = data.transactionsRetry?.find((item: any) => item.return === 'SUCCESS' || item.return === 'FAIL')
                const totalRetry = Number(data.transactionsRetry?.length) || 0
                return (
                    <Space>
                        <Button
                            disabled={isRetried}
                            onClick={() => setModalRetry({ isOpen: true, data })}
                            style={isRetried ? {} : { color: 'white', backgroundColor: '#b49115' }}
                        >
                            Retry
                        </Button>
                        <Button
                            disabled={totalRetry === 0}
                            onClick={() => setModalDetail({ isOpen: true, data })}
                            style={totalRetry === 0 ? {} : { color: 'white', backgroundColor: '#129c37' }}
                        >
                            Detail ({totalRetry})
                        </Button>
                    </Space>
                )
            },
        },
    ]

    const retryColumns: TableProps<any>['columns'] = [
        {
            title: 'TranId',
            dataIndex: 'transaction',
            key: 'tranId',
            render: transaction => transaction.tranId,
        },
        {
            title: 'CpCode',
            dataIndex: 'transaction',
            key: 'cpCode',
            render: transaction => transaction.cpCode,
        },
        {
            title: 'DataPack',
            dataIndex: 'transaction',
            key: 'dataPack',
            render: transaction => transaction.dataPack,
        },
        {
            title: 'Return',
            dataIndex: 'transaction',
            key: 'return',
            render: transaction => transaction.return === 'Refund' ? '' : transaction.return,
        },
    ]

    const detailColumns: TableProps<any>['columns'] = [
        {
            title: 'TranId',
            dataIndex: 'tranId',
            key: 'tranId',
        },
        {
            title: 'ReqTime',
            dataIndex: 'reqTime',
            key: 'reqTime',
            render: (reqTime: number) => dayjs(reqTime).format("YYYY-MM-DD HH:mm:ss"),
        },
        {
            title: 'Msisdn',
            dataIndex: 'msisdn',
            key: 'msisdn',
        },
        {
            title: 'DataPack',
            dataIndex: 'dataPack',
            key: 'dataPack',
        },
        {
            title: 'Return',
            dataIndex: 'return',
            key: 'return',
            render: (status: string) => status === 'Refund' ? '' : status,
        },
    ]

    const fetchListPartner = useCallback(async () => {
        try {
            const { data } = await apiGetListPartner()
            data.partners = data.partners.filter((item: any) => !mapCompanyAccounts[item.cpCode])
            setCustomers(data.partners.map((item: any) => ({ label: item.cpCode, value: item.cpCode })) || [])
        } catch (error) {
            console.log(error)
        }
    }, [])

    const fetchListTransaction = useCallback(async () => {
        if (!search.year || !search.month) return
        try {
            setIsLoading(true)
            const { data } = await apiGetListTransactionRetry({
                cpCode: search?.cpCode,
                year: search.year,
                month: search.month,
                day: search.day,
                page: currentPage,
                pageSize: pageSize,
            })
            setData(data)
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }, [setIsLoading, search, pageSize, currentPage])

    useEffect(() => {
        fetchListPartner()
        fetchListTransaction()
    }, [fetchListPartner, fetchListTransaction])

    const handleSearch = (values: any) => {
        const date = dayjs(values.date)
        setSearch({
            cpCode: values.cpCode || '',
            year: date?.year(),
            month: date?.month() + 1,
            day: values.day || null,
        })
    }

    const handleClearSearch = () => {
        formSearch.resetFields()
        initSearch()
        setSelectedRows([])
    }

    const handleRetry = async () => {
        try {
            setIsLoading(true)
            if (!otpConfirm) {
                toastError('Vui lòng nhập mã xác nhận')
                return
            }
            const { data } = await apiTransactionRetry({
                tranIds: [modalRetry.data?.transaction?.id],
                otpConfirm,
            })
            fetchListTransaction()
            setModalRetry({ isOpen: false, data: null })
            setOtpConfirm('')
            toastSuccess(data.message)
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra, vui lòng thử lại sau')
        } finally {
            setIsLoading(false)
        }
    }

    const handleRetrySelected = async () => {
        try {
            setIsLoading(true)
            if (!otpConfirm) {
                toastError('Vui lòng nhập mã xác nhận')
                return
            }
            const { data } = await apiTransactionRetry({
                tranIds: selectedRows.map((item: any) => item.transaction.id),
                otpConfirm,
            })
            fetchListTransaction()
            setSelectedRows([])
            setIsOpenRetrySelected(false)
            setOtpConfirm('')
            toastSuccess(data.message)
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra, vui lòng thử lại sau')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div>
            <Form form={formSearch} onFinish={handleSearch}>
                <div style={{ display: 'flex', alignItems: 'start', flexWrap: 'wrap', gap: 20 }}>
                    <Form.Item name="cpCode" label={<b>Đối tác</b>}>
                        <Select
                            style={{ minWidth: 170 }}
                            placeholder="Chọn đối tác"
                            defaultValue={''}
                            options={[
                                { label: 'Tất cả', value: '' },
                                ...customers,
                            ]}
                        />
                    </Form.Item>
                    <Form.Item name="date" label="Tháng">
                        <DatePicker
                            defaultValue={dayjs()}
                            format="YYYY-MM"
                            picker="month"
                        />
                    </Form.Item>
                    <Form.Item name="day" label="Ngày" initialValue={dayjs().date()}>
                        <InputNumber min={1} max={31} />
                    </Form.Item>
                    <Space style={{ marginBottom: 24 }}>
                        <Button type='primary' htmlType="submit" icon={<SearchOutlined />}>Tìm kiếm</Button>
                        <Button onClick={handleClearSearch} icon={<UndoOutlined />}></Button>
                    </Space>
                </div>
            </Form>
            <Divider />
            <Button
                disabled={selectedRows.length === 0}
                onClick={() => setIsOpenRetrySelected(true)}
                style={{ marginBottom: 20 }}>
                Retry Selected ({selectedRows.length})
            </Button>
            <Table
                style={{ overflow: 'auto' }}
                rowSelection={{
                    selectedRowKeys: selectedRows.map((item: any) => item.transaction.id),
                    onChange: (_, selectedRows: any) => {
                        setSelectedRows(selectedRows)
                    },
                    getCheckboxProps: (record: any) => ({
                        disabled: record?.transactionsRetry.find((item: any) =>
                            item.return === 'SUCCESS' || item.return === 'FAIL') ? true : false,
                    }),
                }}
                dataSource={data?.transactions}
                columns={sponsorColumns}
                rowKey={(record) => record.transaction.id}
                pagination={false}
                footer={() => (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignContent: 'center' }}>
                        <div>Tổng {data?.total || '0'} giao dịch</div>
                        <Pagination
                            showSizeChanger
                            pageSize={pageSize}
                            onShowSizeChange={(_current, size) => setPageSize(size)}
                            total={data?.total || 0}
                            current={currentPage}
                            onChange={activePage => setCurrentPage(activePage)}
                        />
                    </div>
                )}
            />
            <Modal title='Retry' open={modalRetry.isOpen}
                onCancel={() => setModalRetry({ data: null, isOpen: false })}
                footer={[
                    <Button onClick={handleRetry} type='primary'>
                        Retry
                    </Button>,
                ]}>
                <Divider />
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}>
                    <div>
                        <p><b>TranId</b>: {modalRetry.data?.transaction?.tranId}</p>
                        <p><b>DataPack</b>: {modalRetry.data?.transaction?.dataPack}</p>
                    </div>
                    <div>
                        <p><b>CpCode</b>: {modalRetry.data?.transaction?.cpCode}</p>
                        <p><b>Return</b>: {modalRetry.data?.transaction?.return}</p>
                    </div>
                    <div>
                    </div>
                </div>
                <p>Nhập mã xác nhận</p>
                <Input value={otpConfirm} onChange={e => setOtpConfirm(e.target.value)} />
                <Divider />
            </Modal>
            <Modal title='Danh sách giao dịch retry' open={isOpenRetrySelected}
                onCancel={() => setIsOpenRetrySelected(false)}
                footer={[
                    <Button onClick={handleRetrySelected} type='primary'>
                        Retry
                    </Button>,
                ]}>
                <Divider />
                <div style={{ marginBottom: 20 }}>
                    <Table
                        dataSource={selectedRows}
                        columns={retryColumns}
                        pagination={false}
                    />
                    <p>Nhập mã xác nhận</p>
                    <Input value={otpConfirm} onChange={e => setOtpConfirm(e.target.value)} />
                    <Divider />
                </div>
            </Modal>
            <Modal className='custom-modal-width' title='Detail' open={modalDetail.isOpen}
                onCancel={() => {
                    setModalDetail({ data: null, isOpen: false })
                    setOtpConfirm('')
                }}
                footer={[]}>
                <div style={{ marginBottom: 20 }}>
                    {modalDetail.data?.transactionsRetry?.length > 0 && (
                        <Table
                            style={{ overflow: 'auto' }}
                            dataSource={modalDetail.data?.transactionsRetry}
                            columns={detailColumns}
                            rowKey={(record) => record.id}
                            pagination={false}
                        />
                    )}
                </div>
            </Modal>
        </div>
    )
}
